<template>
	<template v-if="data && statementStillValid && !statementAlreadyAnswered && !statementSent">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<section>
				<h1>Bitte um Stellungnahme</h1>

				<p>Es gab eine Beschwerde oder mehrere Beschwerden zu einem Werbemittel. Wir möchten Sie bitten dazu hier Stellung zu nehmen. </p>
				<p class="text">{{ data.question }}</p>

				<TheBox :title="'Fall Infos'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Falltitel:">
								<span>{{ data.case.title }}</span>
							</el-form-item>

							<el-form-item label="Beschwerdegrund:">
								<span>{{ data.case.category.name }}</span>
							</el-form-item>

							<el-form-item label="Medium:">
								<span>{{ data.case.mediums.map((m) => m.name).join(', ') }}</span>
							</el-form-item>

							<el-form-item label="Reichweite:">
								<span>{{ data.case.reaches.map((r) => r.name).join(', ') }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Erstellungsdatum:">
								<span>{{ $formatDate(data.createdDate) }}</span>
							</el-form-item>

							<el-form-item label="Beschwerden">
								<span>{{ data.case.complaints.length }}</span>
							</el-form-item>

							<el-form-item label="Frist Stellungnahme:">
								<span>{{ data.lastValidDate ? $formatDate(data.lastValidDate) : $formatDate(data.firstValidDate) }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox v-for="complaint in data.case.complaints" :key="complaint.id" :title="'Beschwerde'">
					<div class="text">
						{{ complaint.text }}
					</div>
				</TheBox>
			</section>

			<DetailsMedia
				v-if="statementFiles.filter((f) => !f.isNew).length > 0"
				:files="statementFiles.filter((f) => !f.isNew)"
				:showAddButton="false"
				@addToFiles="null"
				@removeFromFiles="null"
				:pageType="'stellungnahme'"
			/>

			<DetailsLinks
				v-if="statementLinks.filter((f) => !f.isNew).length > 0"
				:links="statementLinks.filter((f) => !f.isNew)"
				:showAddButton="false"
				@addToLinks="null"
				@removeFromLinks="null"
				:pageType="'stellungnahme'"
			/>

			<section id="ihreStellungnahme">
				<h2 class="like-h1">Stellungnahme abgeben</h2>

				<TheBox :title="'Ihre Stellungnahme:'" :titleLevel="3">
					<el-form-item label="" prop="answer">
						<el-input v-model="data.answer" :autosize="{ minRows: 6 }" type="textarea" placeholder="Bitte Stellungnahme hier eingeben." />
					</el-form-item>
				</TheBox>
			</section>
			<section>
				<h2>Optionale Informationen</h2>
			</section>
			<DetailsMedia
				:files="statementFiles.filter((f) => f.isNew)"
				:showAddButton="true"
				@addToFiles="addToFiles"
				@removeFromFiles="removeFromFiles"
				:pageType="'stellungnahme'"
			/>

			<DetailsLinks
				:links="statementLinks.filter((f) => f.isNew)"
				:showAddButton="true"
				@addToLinks="addToLinks"
				@removeFromLinks="removeFromLinks"
				:pageType="'stellungnahme'"
			/>

			<section>
				<el-form-item prop="confirmation" label="">
					<el-checkbox v-model="data.confirmation">
						<strong>DSGVO und Mailzustimmung:</strong> {{ texts.werberat.dsgvo }}
						<RouterLink to="/datenschutz" title="Unsere Datenschutzbestimmungen" target="_blank" class="link">
							{{ texts.werberat.dsgvoLinktext }}
						</RouterLink>
					</el-checkbox>
				</el-form-item>

				<p><strong>Hinweis: </strong>{{ texts.werberat.dsgvoHinweis }}</p>
			</section>

			<section>
				<TheButton type="danger" @buttonclick="answerStatement">Stellungnahme abgeben</TheButton>
			</section>
		</TheForm>

		<DialogExtendStatementDeadline
			v-model:open="openDialogExtendStatementDeadline"
			:statementId="id"
			:deadlineDate="data.firstValidDate"
			@reload="loadStatement"
		/>
	</template>

	<section v-else-if="data && !statementStillValid">
		<h1>Stellungnahmefrist abgelaufen</h1>
		<p>Leider ist die Frist für Ihre Stellungnahme bereits abgelaufen.</p>
	</section>

	<section v-else-if="data && statementAlreadyAnswered">
		<h1>Diese Stellungnahme wurde schon beantwortet</h1>
		<p>Vielen Dank. Sie haben diese Stellungnahme bereits beantwortet.</p>
	</section>

	<section v-else-if="data && statementSent">
		<h1>Ihre Stellungnahme wurde eingereicht</h1>
		<p>Vielen Dank. Ihre Stellungnahme wurde erfolgreich eingereicht.</p>
	</section>

	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import ComplaintsTable from '@/components/tables/ComplaintsTable.vue';
import LogsTable from '@/components/tables/LogsTable.vue';
import StatementsTable from '@/components/tables/StatementsTable.vue';
import { answerStatement, getStatement } from '@/requests/statements';
import { AnswerStatementRequest } from '@/types/formdataTypes';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { Delete, Loading, Plus } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { ElMessage } from 'element-plus';
import { defineComponent, markRaw, ref } from 'vue';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogExtendStatementDeadline from '../components/dialogs/DialogExtendStatementDeadline.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { AuditResponse, DetailFile, DetailLink, FileDto, Gender, LinkDto, StatementResponse, StatementState } from '../types/types';
import { validateCheckbox, validateForm } from '../utils/functions';
import { texts } from '../utils/texts.js';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';

const ElMessageDuration = 3000;

interface StatementResponseWithConfirmation extends StatementResponse {
	confirmation?: boolean;
}

export default defineComponent({
	name: 'StellungnahmeEdit',
	data() {
		return {
			data: null as StatementResponseWithConfirmation | null,
			formId: 'statementForm',
			rules: {
				answer: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				confirmation: [{ validator: validateCheckbox, trigger: 'change' }],
			},
			waiting: false,
			loading: true,
			statementFiles: [] as DetailFile[],
			statementLinks: [] as DetailLink[],
			openDialogExtendStatementDeadline: false,
			logs: [] as AuditResponse[],
			logsLoading: true,
			Gender,
			StatementState,
			statementSent: false,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		Plus,
		Delete,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		ComplaintsTable,
		StatementsTable,
		DialogExtendStatementDeadline,
		LogsTable,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		token() {
			return this.$route.query.token as string;
		},
		statementStillValid() {
			if (!this.data) return false;
			const now = new Date();
			const dueDate = this.data.lastValidDate ? new Date(this.data.lastValidDate) : new Date(this.data.firstValidDate);
			return now < dueDate;
		},
		statementAlreadyAnswered() {
			if (!this.data) return false;
			return this.data.answeredDate !== null;
		},
	},
	methods: {
		async answerStatement() {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const answerStatementRequest: AnswerStatementRequest = {
				statementId: this.id,
				answer: this.data.answer!,
				emailToken: this.token,
			};

			const newLinks = this.statementLinks
				.filter((l) => l.isNew)
				.map((l) => l.link)
				.map((l) => {
					return { name: l.name, url: l.url, type: l.type } as LinkDto;
				});

			if (newLinks.length > 0) answerStatementRequest.links = newLinks;

			const newFiles = this.statementFiles
				.filter((f) => f.isNew)
				.map((f) => f.file)
				.map((f) => {
					return { fileName: f.fileName, contentType: f.contentType, bytes: f.bytes } as FileDto;
				});

			if (newFiles.length > 0) answerStatementRequest.files = newFiles;

			const answerResult = await answerStatement(answerStatementRequest);
			if (answerResult.success) {
				this.statementSent = true;
			} else {
				ElMessage({ message: this.texts.statements.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async loadStatement() {
			const loading = startLoading('#stellungnahme-bearbeiten');

			const result = await getStatement(this.id, this.token ? this.token : '');
			if (result.success) {
				this.data = result.data;
				this.data.confirmation = false;
				this.statementFiles = this.getStatementFiles();
				this.statementLinks = this.getStatementLinks();
			}

			this.loading = false;
			stopLoading(loading);
		},
		addToFiles(file) {
			this.statementFiles.push(file);
		},
		removeFromFiles(file) {
			this.statementFiles = this.statementFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.statementLinks = this.statementLinks.filter((l) => l.link !== link.link);
		},
		addToLinks(link) {
			this.statementLinks.push(link);
		},
		getStatementFiles() {
			if (!this.data) return [];
			let files = [] as DetailFile[];
			this.data.files?.forEach((file) => {
				files.push({
					file: {
						...file,
						bytes: null,
					},
					isNew: false,
				});
			});
			this.data.case.files?.forEach((file) => {
				files.push({
					file: {
						...file,
						bytes: null,
					},
					isNew: false,
				});
			});
			this.data.case.complaints?.forEach((complaint) => {
				complaint.files?.forEach((file) => {
					files.push({
						file: {
							...file,
							bytes: null,
						},
						isNew: false,
					});
				});
			});
			return files;
		},
		getStatementLinks() {
			if (!this.data) return [];
			let links = [] as DetailLink[];
			this.data.links?.forEach((link) => {
				links.push({
					link: link,
					isNew: false,
				});
			});
			this.data.case.links?.forEach((link) => {
				links.push({
					link: link,
					isNew: false,
				});
			});
			this.data.case.complaints?.forEach((complaint) => {
				complaint.links?.forEach((link) => {
					links.push({
						link: link,
						isNew: false,
					});
				});
			});
			return links;
		},
	},
	mounted() {
		this.loadStatement();
	},
});
</script>

<style lang="scss">
#stellungnahme-bearbeiten {
	.text {
		display: inline-block;
		margin: 0;
		margin-bottom: 1.5rem;
	}

	section:first-child {
		margin-top: auto;
	}

	#ihreStellungnahme {
		h3 {
			font-size: 1.75rem;
			font-weight: 500;
		}
	}
}
</style>
